import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import api from "@/lib/api";
import { Search, Scale, Phone, Mail, Globe, MapPin, ShieldCheck, Building2, AlertTriangle, Bookmark, BookmarkPlus, X } from "lucide-react";
import { attorneyText, directoryText, areaLabel } from "@/lib/attorneyI18n";
import { useI18n as useLangCtx } from "@/context/I18nContext";
import { NC_COUNTIES } from "@/lib/ncCounties";
import { useAuth } from "@/context/AuthContext";

export default function AttorneyDirectory() {
  const LANG = useLangCtx().lang;
  const AL = attorneyText(LANG);
  const DL = directoryText(LANG);
  const [params, setParams] = useSearchParams();
  const [areas, setAreas] = useState([]);
  const [attorneys, setAttorneys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(params.get("q") || "");
  const [debouncedQ, setDebouncedQ] = useState(params.get("q") || "");
  const area = params.get("area") || "";
  const county = params.get("county") || "";

  useEffect(() => { api.get("/directory/practice-areas").then(({ data }) => setAreas(data.practice_areas)).catch(() => {}); }, []);
  useEffect(() => { const t = setTimeout(() => setDebouncedQ(q), 350); return () => clearTimeout(t); }, [q]);

  useEffect(() => {
    setLoading(true);
    const query = {};
    if (area) query.practice_area = area;
    if (county) query.county = county;
    if (debouncedQ) query.q = debouncedQ;
    api.get("/directory/attorneys", { params: query })
      .then(({ data }) => setAttorneys(data.attorneys)).finally(() => setLoading(false));
  }, [area, county, debouncedQ]);

  const setArea = (a) => { const p = new URLSearchParams(params); if (!a || a === area) p.delete("area"); else p.set("area", a); setParams(p); };
  const setCounty = (c) => { const p = new URLSearchParams(params); if (!c) p.delete("county"); else p.set("county", c); setParams(p); };

  const { user } = useAuth();
  const [saved, setSaved] = useState([]);
  const loadSaved = () => { if (!user) return; api.get("/directory/saved-searches").then(({ data }) => setSaved(data.searches || [])).catch(() => {}); };
  useEffect(() => { loadSaved(); }, [user]);
  const alreadySaved = saved.some((s) => (s.area || "") === (area || "") && (s.county || "") === (county || ""));
  const saveSearch = async () => {
    if (!area && !county) { toast.error(DL.pickFirst); return; }
    try {
      const { data } = await api.post("/directory/saved-searches", { area, county });
      toast.success(data.duplicate ? DL.dup : DL.savedOk);
      loadSaved();
    } catch (e) { toast.error(e.response?.data?.detail || DL.saveErr); }
  };
  const applySaved = (s) => { const p = new URLSearchParams(); if (s.area) p.set("area", s.area); if (s.county) p.set("county", s.county); setParams(p); };
  const removeSaved = async (s, e) => {
    e.stopPropagation();
    try { await api.delete(`/directory/saved-searches/${s.id}`); setSaved((prev) => prev.filter((x) => x.id !== s.id)); }
    catch { toast.error(DL.removeErr); }
  };

  const [claimFor, setClaimFor] = useState(null);
  const [claim, setClaim] = useState({ claimant_email: "", firm: "", phone: "", email: "", website: "", bio: "" });
  const [claimBusy, setClaimBusy] = useState(false);
  const openClaim = (a) => { setClaimFor(a); setClaim({ claimant_email: "", firm: a.firm || "", phone: a.phone || "", email: a.email || "", website: a.website || "", bio: a.bio || "" }); };
  const submitClaim = async () => {
    if (!claim.claimant_email.trim()) { toast.error(AL.yourEmailReq); return; }
    setClaimBusy(true);
    try {
      await api.post(`/directory/attorneys/${claimFor.id}/claim`, claim);
      toast.success(AL.claimSent);
      setClaimFor(null);
    } catch { toast.error(AL.claimErr); }
    finally { setClaimBusy(false); }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <section className="max-w-7xl mx-auto px-6 pt-16 pb-8">
        <p className="overline text-accent mb-3 flex items-center gap-2"><Scale size={14} /> {DL.over}</p>
        <h1 className="font-serif font-bold text-5xl sm:text-6xl tracking-tighter mb-4">{DL.h1}</h1>
        <p className="text-muted-foreground max-w-2xl">
          {DL.intro}
        </p>

        <div className="mt-8 flex flex-col sm:flex-row gap-3 max-w-2xl">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
            <Input value={q} onChange={(e) => setQ(e.target.value)} data-testid="attorney-search"
              placeholder={DL.search} className="rounded-sm h-12 pl-10" />
          </div>
          <select value={county} onChange={(e) => setCounty(e.target.value)} data-testid="county-filter"
            className="h-12 rounded-sm border-2 border-black/15 bg-background px-3 text-sm sm:w-56">
            <option value="">{DL.allCounties}</option>
            {NC_COUNTIES.map((c) => <option key={c.slug} value={c.name}>{DL.county(c.name)}</option>)}
          </select>
        </div>
        {county && <p className="mt-2 text-sm text-muted-foreground" data-testid="county-active">{DL.showing(county)}</p>}

        {user && (
          <div className="mt-5" data-testid="saved-searches">
            <div className="flex flex-wrap items-center gap-2">
              <Button onClick={saveSearch} disabled={(!area && !county) || alreadySaved} size="sm" variant="outline"
                data-testid="save-search-btn" className="rounded-sm gap-2 h-9">
                {alreadySaved ? <Bookmark size={15} className="fill-current" /> : <BookmarkPlus size={15} />}
                {alreadySaved ? DL.saved : DL.save}
              </Button>
              {saved.map((s) => (
                <button key={s.id} onClick={() => applySaved(s)} data-testid={`saved-search-${s.id}`}
                  className="group inline-flex items-center gap-2 pl-3 pr-2 h-9 border-2 border-accent/40 bg-accent/5 text-sm hover:border-accent transition-colors">
                  <Bookmark size={13} className="text-accent shrink-0" />
                  <span>{s.label}</span>
                  <span role="button" tabIndex={0} onClick={(e) => removeSaved(s, e)} data-testid={`remove-saved-${s.id}`}
                    className="grid place-items-center w-5 h-5 text-muted-foreground hover:text-[#E63946] transition-colors">
                    <X size={13} />
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="mt-6 flex flex-wrap gap-2" data-testid="practice-area-filters">
          <button onClick={() => setArea("")} data-testid="area-all"
            className={`px-4 py-2 border-2 text-sm font-medium transition-colors ${!area ? "border-black bg-primary text-primary-foreground" : "border-black/15 hover:border-black/40"}`}>
            {DL.allAreas}
          </button>
          {areas.map((a) => (
            <button key={a} onClick={() => setArea(a)} data-testid={`area-${a}`}
              className={`px-4 py-2 border-2 text-sm font-medium transition-colors ${area === a ? "border-black bg-primary text-primary-foreground" : "border-black/15 hover:border-black/40"}`}>
              {areaLabel(LANG, a)}
            </button>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 pb-24">
        {loading ? (
          <p className="text-muted-foreground py-12 text-center animate-pulse">{DL.loading}</p>
        ) : attorneys.length === 0 ? (
          <div className="border-2 border-dashed border-black/15 p-12 text-center" data-testid="attorney-empty">
            <Building2 className="mx-auto text-muted-foreground mb-3" size={28} />
            <p className="font-medium">{DL.empty}</p>
            <p className="text-sm text-muted-foreground mt-1">{DL.emptySub}</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-5" data-testid="attorney-grid">
            {attorneys.map((a) => (
              <div key={a.id} data-testid={`attorney-card-${a.id}`}
                className={`p-6 border-2 transition-colors ${a.is_resource ? "border-accent bg-accent/5" : "border-black/10 hover:border-black/25"}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="font-serif text-xl tracking-tight">{a.name}</h3>
                    {a.firm && <p className="text-sm text-muted-foreground">{a.firm}</p>}
                  </div>
                  {a.is_resource
                    ? <Badge className="rounded-sm bg-accent text-accent-foreground shrink-0 gap-1"><ShieldCheck size={12} /> {DL.resource}</Badge>
                    : a.board_certified
                      ? <Badge className="rounded-sm bg-primary text-primary-foreground shrink-0 gap-1"><ShieldCheck size={12} /> {DL.cert}</Badge>
                      : <Badge variant="outline" className="rounded-sm shrink-0">{DL.attorney}</Badge>}
                </div>

                {a.bio && <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{a.bio}</p>}

                {a.practice_areas?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    {a.practice_areas.slice(0, 5).map((p) => (
                      <span key={p} className="text-xs px-2 py-0.5 bg-secondary rounded-sm">{areaLabel(LANG, p)}</span>
                    ))}
                    {a.practice_areas.length > 5 && <span className="text-xs px-2 py-0.5 text-muted-foreground">+{a.practice_areas.length - 5}</span>}
                  </div>
                )}

                <div className="mt-4 pt-4 border-t border-border" data-testid={`attorney-contact-${a.id}`}>
                  <p className="overline text-accent mb-2 text-[0.65rem]">{DL.contact}</p>
                  <div className="grid gap-1.5 text-sm">
                    {(a.city || a.counties?.length > 0) && (
                      <p className="flex items-center gap-2 text-muted-foreground"><MapPin size={14} className="text-accent shrink-0" />
                        {a.city}{a.counties?.length ? `${a.city ? " · " : ""}${LANG === "es" ? "Condado de " : ""}${a.counties.slice(0, 3).join(", ")}${LANG === "es" ? "" : " County"}` : ""}</p>
                    )}
                    {a.phone && <a href={`tel:${a.phone.replace(/[^\d+]/g, "")}`} className="flex items-center gap-2 hover:text-accent" data-testid={`attorney-phone-${a.id}`}><Phone size={14} className="text-accent shrink-0" /> {a.phone}</a>}
                    {a.email && <a href={`mailto:${a.email}`} className="flex items-center gap-2 hover:text-accent break-all"><Mail size={14} className="text-accent shrink-0" /> {a.email}</a>}
                    {a.website && <a href={a.website} target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:text-accent break-all"><Globe size={14} className="text-accent shrink-0" /> {DL.website}</a>}
                    {a.profile_url && <a href={a.profile_url} target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:text-accent" data-testid={`attorney-profile-${a.id}`}><ShieldCheck size={14} className="text-accent shrink-0" /> {DL.bar}{a.bar_id ? ` (#${a.bar_id})` : ""}</a>}
                    {!a.phone && !a.email && !a.website && !a.profile_url && (
                      <p className="text-xs text-muted-foreground italic">{DL.noContact}</p>
                    )}
                  </div>
                  {!a.is_resource && (
                    <button onClick={() => openClaim(a)} data-testid={`claim-listing-${a.id}`}
                      className="mt-3 text-xs font-semibold text-accent hover:underline">
                      {AL.claimLink(a.name.split(" ")[0])}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-10 border-2 border-[#E63946] bg-[#FDECEC] p-4 rounded-sm flex items-start gap-2 max-w-3xl">
          <AlertTriangle size={16} className="text-[#E63946] mt-0.5 shrink-0" />
          <p className="text-xs text-neutral-700 leading-relaxed">
            {DL.disclaimer}
          </p>
        </div>
      </section>

      <Dialog open={!!claimFor} onOpenChange={(o) => !o && setClaimFor(null)}>
        <DialogContent className="rounded-sm max-w-md max-h-[85vh] overflow-y-auto" data-testid="claim-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl">{AL.claimH}</DialogTitle></DialogHeader>
          {claimFor && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">{AL.claimIntro(claimFor.name)}</p>
              <div><Label className="text-xs">{AL.yourEmail}</Label><Input type="email" data-testid="claim-email" value={claim.claimant_email} onChange={(e) => setClaim({ ...claim, claimant_email: e.target.value })} className="rounded-sm mt-1" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">{AL.firmShort}</Label><Input value={claim.firm} onChange={(e) => setClaim({ ...claim, firm: e.target.value })} className="rounded-sm mt-1" /></div>
                <div><Label className="text-xs">{AL.phone}</Label><Input data-testid="claim-phone" value={claim.phone} onChange={(e) => setClaim({ ...claim, phone: e.target.value })} className="rounded-sm mt-1" /></div>
              </div>
              <div><Label className="text-xs">{AL.publicEmail}</Label><Input value={claim.email} onChange={(e) => setClaim({ ...claim, email: e.target.value })} className="rounded-sm mt-1" /></div>
              <div><Label className="text-xs">{AL.website}</Label><Input value={claim.website} onChange={(e) => setClaim({ ...claim, website: e.target.value })} className="rounded-sm mt-1" placeholder="https://" /></div>
              <div><Label className="text-xs">{AL.bio}</Label><Textarea rows={3} value={claim.bio} onChange={(e) => setClaim({ ...claim, bio: e.target.value })} className="rounded-sm mt-1" /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setClaimFor(null)} className="rounded-sm">{AL.cancel}</Button>
            <Button onClick={submitClaim} disabled={claimBusy} data-testid="claim-submit" className="rounded-sm">{AL.submitClaim}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Footer />
    </div>
  );
}
